import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import useActivityHeartbeat from './hooks/useActivityHeartbeat';
import Toast from './components/Toast';
import FrozenModal from './components/FrozenModal';
import BillingBanner from './components/BillingBanner';
import ForcePasswordChange from './components/ForcePasswordChange';
import LegalGate from './legal/LegalGate';
import LegalPage from './legal/LegalPage';
import NavBar from './components/NavBar';
import Sidebar from './components/Sidebar';
import Onboarding from './components/Onboarding';
import LandingPage   from './pages/LandingPage';
import Dashboard     from './pages/Dashboard';
import Inventory       from './pages/Inventory';
import InventoryDetail from './pages/InventoryDetail';
import Sell          from './pages/Sell';
import Debtors       from './pages/Debtors';
import Customers     from './pages/Customers';
import CustomerDetail from './pages/CustomerDetail';
import Transactions   from './pages/Transactions';
import Financials       from './pages/Financials';
import FinancialDetail  from './pages/FinancialDetail';
import RevenueDetail    from './pages/RevenueDetail';
import ProfitDetail     from './pages/ProfitDetail';
import Team             from './pages/Team';
import Settings         from './pages/Settings';
import More             from './pages/More';
import CompanyDashboard  from './pages/CompanyDashboard';
import CompanyFinancials from './pages/CompanyFinancials';
import AddBusiness       from './pages/AddBusiness';
import BusinessPage      from './pages/BusinessPage';
import BusinessKpiDetail  from './pages/BusinessKpiDetail';
import BusinessProductDetail from './pages/BusinessProductDetail';
import CompanyKpiDetail   from './pages/CompanyKpiDetail';
import FundTracker       from './pages/FundTracker';
import Expenses          from './pages/Expenses';
import Products          from './pages/Products';
import Production        from './pages/Production';
import CompanyPurchases  from './pages/CompanyPurchases';
import CompanyExpenses   from './pages/CompanyExpenses';
import CompanyAssets     from './pages/CompanyAssets';
import UpgradePlan       from './pages/UpgradePlan';
import Charts             from './pages/Charts';
import UpdatePrompt from './components/UpdatePrompt';

function PrivateRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div style={{ padding: '2rem', textAlign: 'center', color: '#94a3b8' }}>Loading…</div>;
  return user ? children : <Navigate to="/" replace />;
}

function OwnerRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div style={{ padding: '2rem', textAlign: 'center', color: '#94a3b8' }}>Loading…</div>;
  if (!user) return <Navigate to="/" replace />;
  if (user.role !== 'owner') return <Navigate to="/dashboard" replace />;
  return children;
}

// Accessible to owner AND manager (not plain staff)
function ManagerRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div style={{ padding: '2rem', textAlign: 'center', color: '#94a3b8' }}>Loading…</div>;
  if (!user) return <Navigate to="/" replace />;
  if (user.role !== 'owner' && user.role !== 'manager') return <Navigate to="/dashboard" replace />;
  return children;
}

// Owner/manager on Pro or Enterprise only — used for the financial reports
// suite, which the Starter plan does not include.
function PaidRoute({ children }) {
  const { user, isPro, loading } = useAuth();
  if (loading) return <div style={{ padding: '2rem', textAlign: 'center', color: '#94a3b8' }}>Loading…</div>;
  if (!user) return <Navigate to="/" replace />;
  if (user.role !== 'owner' && user.role !== 'manager') return <Navigate to="/dashboard" replace />;
  if (!isPro) return <Navigate to="/upgrade" replace />;
  return children;
}

function AppRoutes() {
  const { user, legal, reload } = useAuth();
  // Heartbeat only while signed in with a real (non-temporary) password.
  useActivityHeartbeat(!!user && !user.must_change_password);

  // The server says this session still has a temporary password — refresh so
  // the forced-change screen shows.
  React.useEffect(() => {
    const onRequired = () => reload();
    window.addEventListener('sb:password-change-required', onRequired);
    return () => window.removeEventListener('sb:password-change-required', onRequired);
  }, [reload]);

  if (user?.must_change_password) return <ForcePasswordChange />;
  // Current Terms + Privacy Policy must be accepted before using the app.
  if (user && legal && !legal.accepted) return <LegalGate />;

  return (
    <BrowserRouter>
      {user && <Sidebar />}
      <div className={user ? 'app-content' : ''}>
      {user && <BillingBanner />}
      <Routes>
        <Route path="/" element={user ? <Navigate to="/dashboard" replace /> : <LandingPage />} />
        <Route path="/privacy" element={<LegalPage doc="privacy" />} />
        <Route path="/terms"   element={<LegalPage doc="terms" />} />
        <Route path="/dashboard"       element={<PrivateRoute><Dashboard /></PrivateRoute>} />
        <Route path="/inventory"        element={<PrivateRoute><Inventory /></PrivateRoute>} />
        <Route path="/inventory/:id"   element={<PrivateRoute><InventoryDetail /></PrivateRoute>} />
        <Route path="/sell"            element={<PrivateRoute><Sell /></PrivateRoute>} />
        <Route path="/debtors"         element={<PrivateRoute><Debtors /></PrivateRoute>} />
        <Route path="/customers"       element={<PrivateRoute><Customers /></PrivateRoute>} />
        <Route path="/customers/:id"   element={<PrivateRoute><CustomerDetail /></PrivateRoute>} />
        <Route path="/transactions"    element={<PrivateRoute><Transactions /></PrivateRoute>} />
        <Route path="/charts"            element={<Navigate to="/charts/expenses" replace />} />
        <Route path="/charts/:metric"    element={<PaidRoute><Charts /></PaidRoute>} />
        <Route path="/financials"                  element={<PaidRoute><Financials /></PaidRoute>} />
        <Route path="/financials/detail/:type"    element={<PaidRoute><FinancialDetail /></PaidRoute>} />
        <Route path="/financials/revenue"         element={<ManagerRoute><RevenueDetail /></ManagerRoute>} />
        <Route path="/financials/profit"          element={<PaidRoute><ProfitDetail /></PaidRoute>} />
        <Route path="/team"            element={<OwnerRoute><Team /></OwnerRoute>} />
        <Route path="/settings"        element={<OwnerRoute><Settings /></OwnerRoute>} />
        <Route path="/more"            element={<PrivateRoute><More /></PrivateRoute>} />
        <Route path="/company"            element={<OwnerRoute><CompanyDashboard /></OwnerRoute>} />
        <Route path="/company/financials"          element={<OwnerRoute><CompanyFinancials /></OwnerRoute>} />
        <Route path="/company/kpi/:type"           element={<OwnerRoute><CompanyKpiDetail /></OwnerRoute>} />
        <Route path="/company/add-business"  element={<OwnerRoute><AddBusiness /></OwnerRoute>} />
        <Route path="/company/business/:id"               element={<OwnerRoute><BusinessPage /></OwnerRoute>} />
        <Route path="/company/business/:id/kpi/:type"    element={<OwnerRoute><BusinessKpiDetail /></OwnerRoute>} />
        <Route path="/company/business/:id/product/:productId" element={<OwnerRoute><BusinessProductDetail /></OwnerRoute>} />
        <Route path="/company/fund"         element={<OwnerRoute><FundTracker /></OwnerRoute>} />
        <Route path="/company/purchases"  element={<OwnerRoute><CompanyPurchases /></OwnerRoute>} />
        <Route path="/company/expenses"   element={<OwnerRoute><CompanyExpenses /></OwnerRoute>} />
        <Route path="/company/assets"     element={<OwnerRoute><CompanyAssets /></OwnerRoute>} />
        <Route path="/expenses"            element={<ManagerRoute><Expenses /></ManagerRoute>} />
        <Route path="/products"            element={<ManagerRoute><Products /></ManagerRoute>} />
        <Route path="/production"          element={<ManagerRoute><Production /></ManagerRoute>} />
        <Route path="/upgrade"         element={<PrivateRoute><UpgradePlan /></PrivateRoute>} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      </div>
      <Toast />
      <FrozenModal />
      {user && user.onboarded === false && <Onboarding />}
    </BrowserRouter>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <AppRoutes />
      <UpdatePrompt />
    </AuthProvider>
  );
}
